/**
 * Planets, the Moon, the Sun and Pluto — the data behind every "pick a body" preset.
 *
 * Source: NASA Goddard Space Flight Center, Planetary Fact Sheet — Metric
 * (https://nssdc.gsfc.nasa.gov/planetary/factsheet/), read October 2026. Mass is given in
 * 10²⁴ kg, radius as the *volumetric mean radius* in km (the 1-bar level for the gas
 * giants), semimajor axis in 10⁶ km and the sidereal orbit period in days. `gravity` is
 * the fact sheet's mean surface gravity (at the 1-bar level for the gas giants); the Sun's
 * is its equatorial value. The Moon's orbital values are around the Earth.
 *
 * Earth's `gravity` is the standard value g₀ = 9.806 65 m/s², which is what a
 * physics problem means by "g"; NASA's mean 9.820 m/s² is also what `G·M/R²` returns for
 * these inputs, as the tests check.
 */

import { STANDARD_GRAVITY } from "@/lib/tool-kit/physics-fields/constants";

export type BodyId =
  | "sun"
  | "mercury"
  | "venus"
  | "earth"
  | "moon"
  | "mars"
  | "jupiter"
  | "saturn"
  | "uranus"
  | "neptune"
  | "pluto";

export interface Body {
  id: BodyId;
  /** Mass, kg. */
  mass: number;
  /** Volumetric mean radius, m. */
  radius: number;
  /** Mean surface gravity, m/s². */
  gravity: number;
  /** Mean distance from the Sun (from the Earth for the Moon), m. */
  semiMajorAxis: number | null;
  /** Sidereal orbital period, s. */
  orbitalPeriod: number | null;
}

const DAY = 86_400;

export const BODIES: readonly Body[] = [
  {
    id: "sun",
    mass: 1_988_400e24,
    radius: 695_700e3,
    gravity: 274.0,
    semiMajorAxis: null,
    orbitalPeriod: null,
  },
  {
    id: "mercury",
    mass: 0.3301e24,
    radius: 2439.7e3,
    gravity: 3.7,
    semiMajorAxis: 57.909e9,
    orbitalPeriod: 87.969 * DAY,
  },
  {
    id: "venus",
    mass: 4.8673e24,
    radius: 6051.8e3,
    gravity: 8.87,
    semiMajorAxis: 108.21e9,
    orbitalPeriod: 224.701 * DAY,
  },
  {
    id: "earth",
    mass: 5.9722e24,
    radius: 6371.0e3,
    gravity: STANDARD_GRAVITY,
    semiMajorAxis: 149.598e9,
    orbitalPeriod: 365.256 * DAY,
  },
  {
    id: "moon",
    mass: 0.073_46e24,
    radius: 1737.4e3,
    gravity: 1.62,
    semiMajorAxis: 0.3844e9,
    orbitalPeriod: 27.3217 * DAY,
  },
  {
    id: "mars",
    mass: 0.641_69e24,
    radius: 3389.5e3,
    gravity: 3.73,
    semiMajorAxis: 227.956e9,
    orbitalPeriod: 686.98 * DAY,
  },
  {
    id: "jupiter",
    mass: 1898.13e24,
    radius: 69_911e3,
    gravity: 25.92,
    semiMajorAxis: 778.479e9,
    orbitalPeriod: 4332.589 * DAY,
  },
  {
    id: "saturn",
    mass: 568.32e24,
    radius: 58_232e3,
    gravity: 11.19,
    semiMajorAxis: 1432.041e9,
    orbitalPeriod: 10_755.699 * DAY,
  },
  {
    id: "uranus",
    mass: 86.811e24,
    radius: 25_362e3,
    gravity: 9.01,
    semiMajorAxis: 2867.043e9,
    orbitalPeriod: 30_685.4 * DAY,
  },
  {
    id: "neptune",
    mass: 102.409e24,
    radius: 24_622e3,
    gravity: 11.27,
    semiMajorAxis: 4514.953e9,
    orbitalPeriod: 60_189.018 * DAY,
  },
  {
    id: "pluto",
    mass: 0.013_03e24,
    radius: 1188e3,
    gravity: 0.62,
    semiMajorAxis: 5869.656e9,
    orbitalPeriod: 90_560 * DAY,
  },
];

export function findBody(id: string): Body | null {
  return BODIES.find((body) => body.id === id) ?? null;
}
